import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { BasicScrollView } from "@/components/BasicScrollView";
import { RelativePathString, useLocalSearchParams, useRouter } from 'expo-router';
import Head from 'expo-router/head';
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConsts from '@/utilities/AppConsts';
import * as AppUtilities from "@/utilities/AppUtilities";
import Footer from '@/components/Footer';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import JoinAndPay from '@/components/JoinAndPay';
import { AppContext } from '@/components/AppContext';
import { CommunityMembership } from '@/types/community_membership';
import { CustomDivider } from '@/components/CustomDivider';
import { RabblSearchResult } from '@/components/RabblSearchResult';
import { UNKNOWN_COMMUNITY } from '@/types/community';
import { CustomText, CustomTextStyles } from '@/components/CustomText';
import { AppText } from '@/components/AppText';
import { Image } from 'expo-image';
import { Meta } from '@/components/Meta';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    alignItems: 'center',
    backgroundColor: 'transparent',
    gap: 20,
  },
  contentWrapper: {
    padding: 20,
    gap: 20,
  },
});

export default function PublicMember() {
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const [communityMembership, setCommunityMembership] = useState<CommunityMembership | null>(null);
  const domain = useSelector((state: RootState) => state.user.domain);
  const { member_id } = useLocalSearchParams<{ member_id: string }>();
  const [error, setError] = useState<string>();

  const loadInitialData = useCallback(async () => {
    try {
      showSpinner();
      
      const response = await AppUtilities.fetchOptionallyAuthorized(
        "Member.loadInitialData",
        "/rabbl/api/public_profile",
        "POST",
        {
          domain: domain,
          uid: member_id,
        }
      );

      const communityMembershipResult: CommunityMembership = ((response as any).body as any).community_membership;
      setCommunityMembership(communityMembershipResult);
      setError(undefined);

    } catch (e) {
      setError(AppUtilities.getErrors(e));
      //AppUtils.showErrorToast(AppUtilities.getErrors(e), () => loadInitialData(), true);
      //AppUtilities.handleException(e, "Member.loadInitialData");
    } finally {
      hideSpinner();
    }
  }, [member_id, domain]);

  useEffect(() => {
    if (member_id && member_id.length > 0) {
      loadInitialData();
    }
  }, [member_id, loadInitialData]);

  let subheading = "";
  if (communityMembership) {
    subheading = AppUtils.getMemberShortLocation(communityMembership.user_info);
    if (communityMembership.user_info.publicage ?? false) {
      subheading = communityMembership.user_info.age_years + " / " + subheading;
    }
  }

  return (
    <BasicScrollView center={true}>
      <Meta title={communityMembership ? communityMembership.profile.name : "Member"} />
      {
        error && (
          <AppText>{error}</AppText>
        )
      }
      {
        communityMembership && (
          <>
            <CustomText>{communityMembership.community?.name}</CustomText>
            <AppText><AppText style={CustomTextStyles.highlight}>{communityMembership.profile.name}</AppText> is a member of the {communityMembership.community?.name} Rabbl</AppText>
            <Image
              alt={communityMembership.profile.name}
              contentFit='contain'
              style={{
                width: AppConsts.BASIC_MINIMUM_WIDTH,
                height: AppConsts.BASIC_MINIMUM_WIDTH,
                borderWidth: 1,
              }}
              source={{
                uri: communityMembership.profile.visuals && communityMembership.profile.visuals.length > 0 ?
                          AppUtils.createBackendLink('/rabbl/api/visual?thumbnail=yes&uniqueid=' + communityMembership.profile.visuals[0].uniqueid)
                        :
                          require('@/assets/images/butterfly325x325.png'),
              }}
            />
            <AppText>{subheading}</AppText>
            <CustomDivider />
          </>
        )
      }
      <JoinAndPay autoRedirectForLoggedInUser={false} />
      <Footer />
    </BasicScrollView>
  );
}
